import { unlinkPersons } from "@/lib/person-mentions";
import { unlinkCompanies } from "@/lib/company-mentions";
import { z } from "zod";
import type { AgentProgress } from "@/lib/agent-agui";
import { orgRecordEntrySchema, type OrgRecordEntryDraft } from "@/lib/org-record-entry";
import type { StudioProposal } from "@/lib/studio-proposal";

// A Studio notebook is one Markdown text, kept in genesis as the person's own record and exportable as it is.
// HTML comments carry what Markdown itself cannot (what a prompt asked here left behind); a renderer shows none
// of them.
export interface StudioNotebook {
  id: string;
  name: string;
  updatedAt: number;
  text: string;
}

/**
 * Text that comes in from the user or the model must not be read back as a marker: its `<!-- studio:` turns
 * into the entity, which Markdown still shows as "<". The notebook itself carries real markers (the prompts
 * asked in it), so escaping happens where text enters, not when the notebook is saved.
 */
export function escapeMarkers(text: string): string {
  return text.replace(/<!-- (\/?)studio:/g, "&lt;!-- $1studio:");
}

/** A card a prompt left in the notebook: a proposal of records, or a draft for an organizational record. */
export type StudioCard = { kind: "studioProposal"; data: StudioProposal } | { kind: "orgRecordEntry"; data: OrgRecordEntryDraft };

/** A prompt asked in the notebook and what came back, kept in the notebook where it was asked. */
export interface StudioAsk {
  id: string;
  at: number;
  question: string;
  answer: string;
  cards: StudioCard[];
  steps: AgentProgress[];
  // The notebook's final version, written by the assistant on the user's request: it is offered for export.
  final?: boolean;
}

/**
 * A document the user brought in from their documents (Save · Documents): its text as the documents extraction
 * read it (OCR or text extraction), kept in the notebook, so the notebook carries it as context without reading
 * the document again.
 */
export interface StudioDocument {
  // The document in the user's documents.
  documentId: string;
  name: string;
  text: string;
}

// `raw` is the block exactly as the notebook has it, so the notebook can be put back together after a note changes.
export type NotebookSegment =
  | { kind: "text"; text: string }
  | ({ kind: "ask"; raw: string } & StudioAsk)
  | ({ kind: "document"; raw: string } & StudioDocument);

const ASK_START = /^<!-- studio:ask (\{.*\}) -->$/;
const ASK_END = /^<!-- \/studio:ask -->$/;
const CARD = /^<!-- studio:card (\{.*\}) -->$/;
const DOCUMENT_START = /^<!-- studio:document (\{.*\}) -->$/;
const DOCUMENT_END = /^<!-- \/studio:document -->$/;
const documentMeta = z.object({ documentId: z.string().min(1), name: z.string() });
const askMeta = z.object({
  id: z.string().min(1),
  at: z.number(),
  question: z.string(),
  steps: z.array(z.object({ phase: z.string(), params: z.record(z.string(), z.string()).default({}) }).passthrough()).default([]),
  final: z.boolean().optional(),
});
const cardData = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("studioProposal"),
    data: z.object({
      persons: z.array(z.any()),
      companies: z.array(z.any()),
      rejected: z.array(z.any()).default([]),
      notebookName: z.string().optional(),
      sources: z.array(z.object({ id: z.string(), text: z.string() })).optional(),
    }),
  }),
  z.object({ kind: z.literal("orgRecordEntry"), data: orgRecordEntrySchema }),
]);

// A marker's JSON must not close its comment early.
const markerJson = (value: unknown) => JSON.stringify(value).replace(/-->/g, "--\\u003e");

function parseJson<T>(schema: z.ZodType<T>, json: string): T | null {
  try {
    const parsed = schema.safeParse(JSON.parse(json));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** The block a finished prompt leaves in the notebook: readable as a quote and its answer, the rest in markers. */
export function askToMarkdown(ask: StudioAsk): string {
  return [
    `<!-- studio:ask ${markerJson({ id: ask.id, at: ask.at, question: ask.question, steps: ask.steps, ...(ask.final ? { final: true } : {}) })} -->`,
    ...escapeMarkers(ask.question).split("\n").map((line) => `> ${line}`),
    "",
    escapeMarkers(ask.answer.trim()),
    ...ask.cards.map((card) => `<!-- studio:card ${markerJson(card)} -->`),
    "<!-- /studio:ask -->",
  ].join("\n");
}

/** The block a document brought in leaves in the notebook: its name as a line to read, its text, the rest in markers. */
export function documentToMarkdown(document: StudioDocument): string {
  return [
    `<!-- studio:document ${markerJson({ documentId: document.documentId, name: document.name })} -->`,
    escapeMarkers(document.text.trim()),
    "<!-- /studio:document -->",
  ].join("\n");
}

/**
 * The notebook's text in order: the user's text and, between it, the prompts asked here and the documents
 * brought in. Tolerant, since the text can be edited by hand: an unfinished block ends at the next one or at
 * the end, and a marker whose JSON does not parse is plain text.
 */
export function notebookSegments(text: string): NotebookSegment[] {
  const segments: NotebookSegment[] = [];
  let buffer: string[] = [];
  let ask: { meta: z.infer<typeof askMeta>; lines: string[]; cards: StudioAsk["cards"]; raw: string[] } | null = null;
  let document: { meta: z.infer<typeof documentMeta>; lines: string[]; raw: string[] } | null = null;
  const closeDocument = () => {
    if (!document) return;
    segments.push({ kind: "document", ...document.meta, text: document.lines.join("\n").trim(), raw: document.raw.join("\n").trim() });
    document = null;
  };
  const flushText = () => {
    const part = buffer.join("\n").trim();
    if (part) segments.push({ kind: "text", text: part });
    buffer = [];
  };
  const closeAsk = () => {
    if (!ask) return;
    let body = ask.lines;
    while (body.length && !body[0]!.trim()) body = body.slice(1);
    // The quoted question at the top is for reading the text; the marker holds the question itself.
    while (body.length && body[0]!.startsWith(">")) body = body.slice(1);
    const { steps, final, ...meta } = ask.meta;
    segments.push({
      kind: "ask", ...meta, ...(final ? { final } : {}), steps: steps as AgentProgress[], answer: body.join("\n").trim(), cards: ask.cards,
      raw: ask.raw.join("\n").trim(),
    });
    ask = null;
  };
  for (const line of text.replace(/\r\n/g, "\n").split("\n")) {
    const trimmed = line.trim();
    const start = ASK_START.exec(trimmed);
    const meta = start ? parseJson(askMeta, start[1]!) : null;
    if (meta) {
      if (ask) closeAsk();
      else if (document) closeDocument();
      else flushText();
      ask = { meta, lines: [], cards: [], raw: [line] };
      continue;
    }
    const documentStart = !ask ? DOCUMENT_START.exec(trimmed) : null;
    const opened = documentStart ? parseJson(documentMeta, documentStart[1]!) : null;
    if (opened) {
      if (document) closeDocument();
      else flushText();
      document = { meta: opened, lines: [], raw: [line] };
      continue;
    }
    if (document) {
      document.raw.push(line);
      if (DOCUMENT_END.test(trimmed)) closeDocument();
      else document.lines.push(line);
      continue;
    }
    if (ask) {
      ask.raw.push(line);
      if (ASK_END.test(trimmed)) {
        closeAsk();
        continue;
      }
      const card = CARD.exec(trimmed);
      const data = card ? parseJson(cardData, card[1]!) : null;
      if (data) ask.cards.push(data as StudioCard);
      else ask.lines.push(line);
      continue;
    }
    buffer.push(line);
  }
  closeAsk();
  closeDocument();
  flushText();
  return segments;
}

// The names a proposal card carries, for a line in a file where the card itself cannot be.
function proposalNames(card: StudioCard): string[] {
  if (card.kind !== "studioProposal") return [];
  const persons = card.data.persons.map((person) => [person.givenName, person.familyName].filter(Boolean).join(" "));
  return [...persons, ...card.data.companies.map((company) => company.name)].filter(Boolean);
}

/**
 * The notebook as a file to hand on: its title and the user's text, with or without the prompts asked in it.
 * Nothing of the markers goes out: a prompt reads as a quote with its answer, a card as one line naming what
 * it proposed.
 */
export function exportMarkdown(book: StudioNotebook, options: { withAnswers: boolean; proposalLabel: string; documentLabel?: string }): string {
  const parts = notebookSegments(book.text).flatMap((segment) => {
    if (segment.kind === "text") return [segment.text];
    // A document is context like the answers: along with them, as its name and its text.
    if (segment.kind === "document") return options.withAnswers ? [`*${options.documentLabel ?? "Document"}: ${segment.name}*\n\n${segment.text}`] : [];
    if (!options.withAnswers) return [];
    const names = segment.cards.flatMap(proposalNames);
    return [
      [
        segment.question.split("\n").map((line) => `> ${line}`).join("\n"),
        unlinkCompanies(unlinkPersons(segment.answer)),
        names.length ? `*${options.proposalLabel}: ${names.join(", ")}*` : "",
      ].filter(Boolean).join("\n\n"),
    ];
  });
  return `${[`# ${book.name.replace(/\s+/g, " ")}`, ...parts].join("\n\n")}\n`;
}

/**
 * The passage ids ("[s1]", "[s4, s5]") are for citing in a tool call. The prompt forbids them in a reply, but
 * a model still writes them now and then, so an answer shown or kept in the notebook loses them here, along
 * with a dash that only led up to one.
 */
// `s…` a passage of the user's text, `a…` an answer kept in the notebook, `d…` a document brought in (see
// notebookSources), each also as code, in backticks.
const TICK = "`?";
const PASSAGE_REF = String.raw`${TICK}(?:(?:note|notiz|answer|antwort|document|dokument)[ \t]+)?[sad]\d+${TICK}`;
const PASSAGE_ID = String.raw`${TICK}\[${PASSAGE_REF}(?:[ \t]*[,;–-][ \t]*${PASSAGE_REF})*\]${TICK}`;

export function stripPassageIds(text: string): string {
  return text
    // A bare id in round brackets, "(d1)" or "(s2, a1)", goes as a whole like its bracketed form.
    .replace(new RegExp(String.raw`[ \t]*\([ \t]*${PASSAGE_REF}(?:[ \t]*[,;–-][ \t]*${PASSAGE_REF})*[ \t]*\)`, "gi"), "")
    // "([s1]: quote)" keeps its bracket and loses the id with its colon; "([s1])" goes as a whole.
    .replace(new RegExp(String.raw`[ \t]*\([ \t]*${PASSAGE_ID}[ \t]*\)`, "gi"), "")
    .replace(new RegExp(String.raw`\([ \t]*${PASSAGE_ID}[ \t]*:?[ \t]*`, "gi"), "(")
    .replace(new RegExp(String.raw`[ \t]*(?:[–-][ \t]*)?${PASSAGE_ID}`, "gi"), "");
}

/** A model asked for Markdown often wraps it in a ```markdown fence anyway; the notebook wants the Markdown itself. */
export function unfenceMarkdown(text: string): string {
  const fenced = /^```(?:markdown|md)?[ \t]*\n([\s\S]*?)\n```\s*$/.exec(text.trim());
  return fenced ? fenced[1]!.trim() : text.trim();
}

/**
 * The notebook with one note changed: a note is everything the user wrote between two prompts, found by its place
 * among the notebook's parts. It has to still read as expected, else null: the text moved under the editor, and
 * changing something else would be worse than not saving. The prompts in it stay exactly as they were.
 */
export function replaceNote(text: string, segment: number, expected: string, next: string): string | null {
  const segments = notebookSegments(text);
  const part = segments[segment];
  if (part?.kind !== "text" || part.text !== expected || !next.trim()) return null;
  return segments
    .map((item, index) => (index === segment ? escapeMarkers(next.trim()) : item.kind === "text" ? item.text : item.raw))
    .join("\n\n");
}

/**
 * The notebook with one card of a kept prompt changed (the user edited a proposal before taking it over). The
 * block is written anew from what it holds, so the question, the answer and the steps stay as they were;
 * null when the prompt or the card is not there (any more).
 */
export function replaceCard(text: string, askId: string, cardIndex: number, card: StudioCard): string | null {
  const segments = notebookSegments(text);
  const at = segments.findIndex((segment) => segment.kind === "ask" && segment.id === askId);
  const ask = segments[at];
  if (ask?.kind !== "ask" || !ask.cards[cardIndex]) return null;
  const { kind: _kind, raw: _raw, ...kept } = ask;
  // A card keeps its kind: an edit changes what it holds, never what it is.
  if (ask.cards[cardIndex]!.kind !== card.kind) return null;
  const cards = ask.cards.map((kept, index) => (index === cardIndex ? card : kept));
  return segments
    .map((item, index) => (index === at ? askToMarkdown({ ...kept, cards }) : item.kind === "text" ? item.text : item.raw))
    .join("\n\n");
}

/** Only what the user wrote: the text the assistant cites and every proposal is checked against. */
export function notebookNotes(text: string): string {
  return notebookSegments(text)
    .flatMap((segment) => (segment.kind === "text" ? [segment.text] : []))
    .join("\n\n");
}

/**
 * The notebook as the assistant cites it: passages split at headings and blank lines, numbered in order.
 * The user writes one text; the passages exist so that a proposed value can be checked against the part of
 * the text it claims to come from, not against the whole notebook
 * (@external-adr AGENT-SERVICE-0036-the-studio-proposes-records-the-user-decides). A heading keeps the
 * paragraphs under it.
 */
export function notebookPassages(text: string): { id: string; text: string }[] {
  const passages: string[] = [];
  let underHeading = false;
  for (const block of text.replace(/\r\n/g, "\n").split(/\n\s*\n/)) {
    const trimmed = block.trim();
    if (!trimmed) continue;
    if (/^#{1,6} /.test(trimmed)) {
      passages.push(trimmed);
      underHeading = true;
    } else if (underHeading) {
      passages[passages.length - 1] += `\n\n${trimmed}`;
    } else {
      passages.push(trimmed);
    }
  }
  return passages.map((passage, index) => ({ id: `s${index + 1}`, text: passage }));
}

// Within the BFF's bounds for the notebook a turn carries (200 passages, 20000 characters each).
const MAX_ANSWER_SOURCES = 20;
const MAX_DOCUMENT_SOURCES = 20;
const MAX_SOURCE_CHARS = 20_000;

/**
 * What the assistant may cite for a proposal: the user's passages (s1..), the documents brought in (d1.., in
 * the notebook's order, each read with its name; Marvin, 2026-09-29) and the answers kept in the notebook
 * (a1.., the newest first), so that a find from a web or PubMed search can become a proposal in the next
 * message (Marvin, 2026-09-25). An answer reads with its question, so the model and the check know what it
 * answered. The final version is the notes rewritten and adds nothing, so it stays out.
 */
export function notebookSources(text: string): { id: string; text: string }[] {
  const documents = notebookSegments(text)
    .flatMap((segment) => (segment.kind === "document" && segment.text.trim() ? [segment] : []))
    .slice(0, MAX_DOCUMENT_SOURCES)
    .map((document, index) => ({
      id: `d${index + 1}`,
      text: `„${document.name}“\n\n${document.text.trim()}`.slice(0, MAX_SOURCE_CHARS),
    }));
  const answers = notebookSegments(text)
    .flatMap((segment) => (segment.kind === "ask" && !segment.final && segment.answer.trim() ? [segment] : []))
    .reverse()
    .slice(0, MAX_ANSWER_SOURCES)
    .map((ask, index) => ({
      id: `a${index + 1}`,
      text: `„${ask.question.trim()}“\n\n${unlinkCompanies(unlinkPersons(stripPassageIds(ask.answer))).trim()}`.slice(0, MAX_SOURCE_CHARS),
    }));
  const notes = notebookPassages(notebookNotes(text)).map((passage) => ({ ...passage, text: passage.text.slice(0, MAX_SOURCE_CHARS) }));
  return [...notes, ...documents, ...answers];
}

export function notebookFileName(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${slug || "notebook"}.md`;
}
